import { AR_PANEL, LECTERN, type CueMap, type Lesson } from '@vrlearning/lesson-schema';
import type { LoadedLesson } from '../api/lessons-client.js';
import type { ExtraPressable } from '../render/ui.js';
import { BEKIJKEN_IDLE_MS, IdleTimer } from './idle.js';

/**
 * A school's lesson played solo on one of the platform's test headsets (Steekproef's "Open in Quest",
 * ADR-058 amended 15 Oct 2026). The ordinary player, as a school's headset plays the lesson -- its
 * rendering, panels, models, sounds and voice -- but the pupil steps through it alone, as in a test
 * drive, and nothing is recorded: no session, no results, no name asked. A marker on the lectern
 * says so, beside a Sluiten; the lesson's last "Klaar ✓", Sluiten, leaving immersive mode or half
 * an hour without input: back to the lobby.
 */
export const LESSON_PREVIEW_TEXT = {
  marker: 'Voorbeeld — telt niet mee',
  close: 'Sluiten',
  /** Said once on the lobby card afterwards. */
  ended: 'Het voorbeeld is afgelopen. Er is niets opgeslagen.',
} as const;

export type MarkerBox = { x: number; y: number; width: number; height: number };

/** The top edge of the panel at its full height, in its group's plane. */
export const fullPanelTop = (mode: Lesson['mode']): number =>
  mode === 'ar' ? AR_PANEL.panelY + AR_PANEL.height / 2 : LECTERN.panelY + LECTERN.height / 2;

/**
 * Where the marker and its Sluiten hang, in the lectern's (or the AR panel's) own plane: one row just
 * above the panel's top edge -- `top`, the VR panel being only as tall as its words; its full height
 * when not given -- the marker on the left and Sluiten on the right, both inside the panel's width.
 * On the VR lectern they leave the middle free, where "Sla over" stands 0.1 m above the full panel in
 * a Spannend part (0.36 m wide).
 */
export function previewMarkerLayout(
  mode: Lesson['mode'],
  top: number = fullPanelTop(mode),
): { marker: MarkerBox; close: MarkerBox } {
  if (mode === 'ar') {
    const height = 0.06;
    const y = top + 0.01 + height / 2;
    const left = -AR_PANEL.width / 2;
    const right = AR_PANEL.width / 2;
    return {
      marker: { x: left + 0.19, y, width: 0.38, height },
      close: { x: right - 0.09, y, width: 0.18, height },
    };
  }
  const height = 0.1;
  const y = top + 0.02 + height / 2;
  const left = -LECTERN.width / 2;
  const right = LECTERN.width / 2;
  return {
    marker: { x: left + 0.23, y, width: 0.44, height },
    close: { x: right - 0.15, y, width: 0.3, height },
  };
}

/** What the player is asked to run: never recorded, never a class's. */
export type LessonPreviewRun = {
  recorded: false;
  media: LoadedLesson['media'];
  variants: LoadedLesson['variants'];
  cues: CueMap;
  models: LoadedLesson['models'];
  /** Offered as "Klaar ✓" on the last step: the preview is over. */
  onFinish: () => void;
};

/** What the preview needs of the running lesson (main.ts's `current`). */
export type LessonPreviewTarget = {
  readonly ui: {
    readonly group: unknown;
    setExtraButtons(buttons: readonly ExtraPressable[]): void;
  };
};

/**
 * Plays the lesson and hangs the marker on it: `run` is main.ts's player (`recorded: false`, so an
 * answer goes nowhere), `hangMarker` the drawn marker with its Sluiten (render/preview-marker.ts).
 * Every input touches the idle timer. Returns how to take the marker and the timer away again; the
 * lesson itself is taken down by the lobby (leaveLesson).
 */
export function playLessonPreview<T extends LessonPreviewTarget>(input: {
  loaded: LoadedLesson;
  run: (lesson: Lesson, options: LessonPreviewRun) => T;
  hangMarker: (target: T, onClose: () => void) => () => void;
  /** Back to the lobby, with `notice` said there once. */
  onClose: (notice?: string) => void;
  /** Calls its argument on every press, click or key; returns how to stop listening. */
  listen: (touch: () => void) => () => void;
  idleMs?: number;
}): { target: T; undo: () => void } {
  const { loaded } = input;
  const target = input.run(loaded.lesson, {
    recorded: false,
    media: loaded.media,
    variants: loaded.variants,
    cues: loaded.cues,
    models: loaded.models,
    // After the press is handled: "Klaar ✓" redraws its own panel when the handler returns.
    onFinish: () => setTimeout(() => input.onClose(LESSON_PREVIEW_TEXT.ended), 0),
  });
  const idle = new IdleTimer(input.idleMs ?? BEKIJKEN_IDLE_MS, () => input.onClose());
  const touch = (): void => idle.touch();
  const unlisten = input.listen(touch);
  const take = input.hangMarker(target, () => input.onClose());
  idle.touch();
  return {
    target,
    undo: () => {
      idle.stop();
      unlisten();
      take();
    },
  };
}
